/**
 * OriginsSelect component provides a scenario-dependent origin selection dropdown
 * with GraphQL integration and specific filtering for assignation types.
 *
 * Key features:
 * - Dynamic origin fetching based on selected scenario
 * - Filtered to show only visible assignation-type origins
 * - Formatted display combining ID and label
 * - Loading state handling with skeleton loader
 * - Error state fallback with empty selection
 */

import React, { FC, useCallback, useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Pill, SelectPagination, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { BaseQuery, ProcessStat } from "@models/ISchema";
import processStatsBaseQuery from "../../graphql/processStats.query.graphql";
import styles from "./originsSelect.module.scss";

/**
 * Props interface for OriginsSelect component
 * @property {any} scenarioValue - Selected scenario to filter origins
 * @property {any} originValue - Currently selected origin
 * @property {function} onChange - Callback function triggered when selection changes
 */
interface SelectProps {
  onlyProductive?: boolean;
  workspaceValue: any;
  scenarioValue: any;
  originValue: any;
  onChange: (value: any) => void;
}

/**
 * Renders an origin selection dropdown filtered by the selected scenario
 * Only shows visible origins of type ASSIGNATION
 *
 * @param {SelectProps} props Component props
 * @param {any} props.scenarioValue Selected scenario for filtering origins
 * @param {function} props.onChange Callback for selection changes
 * @param {any} props.originValue Currently selected origin
 * @returns {JSX.Element} Styled select component or loading/error state
 */
const OriginsSelect: FC<SelectProps> = ({
  onlyProductive,
  scenarioValue,
  onChange,
  originValue,
  workspaceValue,
}) => {
  const client = useApolloClient();
  const [prevSearchTerm, setPrevSearchTerm] = useState("");
  const [page, setPage] = useState(0);

  const { t } = useTranslation();

  useEffect(() => {
    // Reset page and search term when scenario changes
    setPage(0);
    setPrevSearchTerm("");
  }, [scenarioValue, workspaceValue, onlyProductive]);

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      try {
        const variables = {
          query: processStatsBaseQuery,
          variables: {
            filter: {
              pagination: {
                page,
                size: 10,
                searchTerm,
              },
              fields: {
                scenarioId: scenarioValue
                  ? {
                      eq: scenarioValue?.value,
                    }
                  : undefined,
                attributes: {
                  isVisible: {
                    is: true,
                  },
                  isProductive: onlyProductive !== undefined ? { is: onlyProductive } : undefined,
                },
                processType: {
                  eq: "ASSIGNATION",
                },
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const baseQuery = data?.base as BaseQuery;
        const pageInfo = baseQuery?.processStats?.pageInfo;
        const processStatsItems = baseQuery?.processStats?.items ?? [];

        const options = processStatsItems.map(item => ({
          label: `${item?.processId} - ${item?.label}`,
          value: item?.processId,
          self: item,
        }));

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client, page, scenarioValue, onlyProductive],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = prevSearchTerm === newSearchTerm ? page + 1 : 1;

    setPrevSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await requestItems(newSearchTerm, newSearchPage);

    return {
      options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  return (
    <SelectPagination
      key={`${workspaceValue?.value}-${scenarioValue?.value}-${onlyProductive ?? ""}`}
      className={styles.originSelect}
      label={t("context-modal.origin")}
      maxMenuHeight={200}
      loadOptions={loadOptions}
      placeholder={t("context-modal.select-origin")}
      formatOptionLabel={(option: any) => {
        return (
          <div className={styles.originSelect_option}>
            <div className={styles.optionDetails}>
              <Text className={styles.optionDetails_title} type="sm">
                {option?.self?.processId ?? option?.value ?? "-"}{" "}
                {(!!option?.self?.label || option?.label) && (
                  <span className={styles.titleLabel}>
                    {option?.self?.label ?? option?.label ?? ""}
                  </span>
                )}
              </Text>

              <Text className={styles.optionDetails_description} type="xs">
                {t("context-modal.details.workspace-short")}:{" "}
                <span className={styles.descriptionLabel}>
                  {option?.self?.scenario?.workspace?.name ?? workspaceValue?.label ?? "-"}
                </span>{" "}
                | {t("context-modal.details.scenario-short")}:{" "}
                <span className={styles.descriptionLabel}>
                  {option?.self?.scenario?.id ?? scenarioValue?.value ?? "-"}{" "}
                  {option?.self?.scenario?.name ?? scenarioValue?.label ?? "-"}
                </span>
              </Text>
            </div>

            <div className={styles.optionStatus}>
              {!!option?.self?.attributes && option?.self?.attributes?.isProductive && (
                <Pill
                  className={styles.optionStatus_pill}
                  icon="star"
                  iconFilled
                  iconColor="neutral-70"
                >
                  {t("context-modal.details.productive")}
                </Pill>
              )}

              {!!option?.self?.attributes && (
                <Pill className={styles.optionStatus_pill}>
                  {option?.self?.attributes?.isReal
                    ? t("context-modal.details.origin-real")
                    : t("context-modal.details.origin-simulated")}
                </Pill>
              )}
            </div>
          </div>
        );
      }}
      value={originValue}
      isOptionDisabled={(option: any) => {
        const processStat = option?.self as ProcessStat;
        return !processStat?.scenario || !processStat?.scenario?.workspace;
      }}
      isOptionSelected={(option: any) => {
        const processStat = option?.self as ProcessStat;
        const selectedProcessId = originValue?.self?.processId ?? originValue?.self?.id;
        const selectedScenarioId = originValue?.self?.scenarioId ?? originValue?.self?.scenario?.id;
        const isSelectedProcessId = processStat?.processId === selectedProcessId;

        if (!selectedScenarioId) {
          return isSelectedProcessId;
        }

        return isSelectedProcessId && processStat?.scenarioId === selectedScenarioId;
      }}
      escapeClearsValue
      onChange={(value: any): void => onChange(value)}
      filterOption={() => true}
      debounceTimeout={500}
    />
  );
};

export default OriginsSelect;
